import _ from 'lodash';
import SubmissionError from './SubmissionError';
import Notifications from './utils/Notifications';

export const defaultErrorMessage =
  'Something went wrong. Please try again later.';

function showError(error, t) {
  Notifications.error(t('Error'), error, t);
}

export class ErrorWithHumanMessage extends Error {
  constructor(humanErrorMessage, ...rest) {
    super(humanErrorMessage, ...rest);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ErrorWithHumanMessage);
    } else {
      this.stack = Error().stack;
    }
    this.humanErrorMessage = humanErrorMessage;
  }
}
export class ApiFakeError extends Error {
  constructor(graphQlErrorMessage, ...rest) {
    super(graphQlErrorMessage, ...rest);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiFakeError);
    } else {
      this.stack = Error().stack;
    }
    this.graphQLErrors = graphQlErrorMessage;
  }
}

export class HumanError extends Error {
  constructor(message, ...rest) {
    super(message, ...rest);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiFakeError);
    } else {
      this.stack = Error().stack;
    }
    this.graphQLErrors = [{ formErrors: { message } }];
  }
}

export async function displayFormErrors(mutationPromise, t) {
  return await mutationPromise.catch(error => {
    if (error.graphQLErrors) {
      const errors = [];
      for (const gqlError of error.graphQLErrors) {
        if (gqlError.formErrors) {
          errors.push(
            ...Object.keys(gqlError.formErrors).map(field => {
              const value = t(gqlError.formErrors[field]);
              return field ? `${field}: ${value}` : value;
            }),
          );
        }
      }
      showError(errors.length ? errors : defaultErrorMessage, t);
      return true;
    }
    return false;
  });
}

const defaultNotificationCallback = t => errors =>
  Object.values(errors).forEach(message => {
    Notifications.error(t('Error'), message, t);
  });

export async function handleFormErrors(
  mutationPromise,
  t,
  notificationCallback = defaultNotificationCallback(t),
  customErrorExtract = null,
) {
  try {
    return await mutationPromise;
  } catch (error) {
    console.error(error);
    const errors = extractGraphqlErrors(error, t, customErrorExtract) || {
      _error: t(defaultErrorMessage),
    };

    if (errors['']) {
      errors._error = errors[''];
      delete errors[''];
    }

    notificationCallback && notificationCallback(errors);
    throw new SubmissionError(recomposeGraphqlErrors(errors));
  }
}

export function extractGraphqlErrors(rootError, t, customErrorExtract = null) {
  const errors = {};

  if (rootError.graphQLErrors) {
    for (const gqlError of rootError.graphQLErrors) {
      if (gqlError.formErrors) {
        Object.assign(
          errors,
          _.mapValues(gqlError.formErrors, value => {
            if (customErrorExtract) {
              return customErrorExtract(value);
            }

            if (Array.isArray(value)) {
              const [key, params] = value;
              return t(key, params);
            }

            return value;
          }),
        );
      }
    }
  }

  return errors;
}

function recomposeGraphqlErrors(errors = {}) {
  const result = _.transform(errors, (result, value, key) =>
    _.set(result, key, value),
  );

  return _.isEmpty(result) ? null : result;
}
